/**
 * SOLOMON — LEAD DETAIL
 * Single-lead view inside LeadsView. Shows property + owner, debounced notes
 * autosave, state-change with reason, state history timeline, source metadata.
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import LeadCalculator from './LeadCalculator';
import DispositionLogger from './DispositionLogger';
import DealStageStepper from './DealStageStepper';
import CostSubtitle from '../CostSubtitle.jsx';
import ConfirmCostDialog from '../ConfirmCostDialog.jsx';
import {
  STATE_LABELS,
  STATE_CLASS,
  formatRelativeTime,
  formatCurrency,
} from './leadsConstants';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';

function ChevronLeft() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function CaretDown({ open }) {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
         style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }}>
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

export default function LeadDetail({ leadId, onBack, onNavigate }) {
  const { authFetch } = useAuth();

  const [lead, setLead]       = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  // Notes draft + save state
  const [notesDraft, setNotesDraft] = useState('');
  const notesInitialRef = useRef('');
  const notesSaveTimer  = useRef(null);
  const [notesStatus, setNotesStatus] = useState(null); // 'saving' | 'saved' | 'error'

  // Collapsibles
  const [showHistory, setShowHistory] = useState(true);
  const [showMeta, setShowMeta]       = useState(false);

  // Enrichment state
  const [enriching, setEnriching]       = useState(false);
  const [enrichError, setEnrichError]   = useState(null);
  const [enrichStatus, setEnrichStatus] = useState(null);
  const [enrichSuppressed, setEnrichSuppressed] = useState(null);
  const [enrichDialogOpen, setEnrichDialogOpen] = useState(false);

  // Cost map

  // ── Fetch lead + history ────────────────────────────────────
  const fetchLead = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [leadRes, histRes] = await Promise.all([
        authFetch(`${API_BASE}/api/leads/${leadId}`),
        authFetch(`${API_BASE}/api/leads/${leadId}/history`),
      ]);
      const leadData = await leadRes.json();
      const histData = await histRes.json();
      if (!leadRes.ok || !leadData.success) throw new Error(leadData.error || `HTTP ${leadRes.status}`);
      if (!histRes.ok || !histData.success) throw new Error(histData.error || `HTTP ${histRes.status}`);
      setLead(leadData.data);
      setHistory(histData.data || []);
      const initial = leadData.data?.notes || '';
      setNotesDraft(initial);
      notesInitialRef.current = initial;
      setNotesStatus(null);
    } catch (err) {
      setError(err.message || 'Failed to load lead');
    } finally {
      setLoading(false);
    }
  }, [authFetch, leadId]);

  useEffect(() => { fetchLead(); }, [fetchLead]);

  // Allow embedded children (e.g., LeadCalculator after Refresh ARV)
  // to request a parent-level reload via a global event. Scoped to this
  // leadId so reloads on other leads are ignored.
  useEffect(() => {
    const handler = (e) => {
      if (e?.detail?.leadId === leadId) fetchLead();
    };
    window.addEventListener('solomon:reload-lead', handler);
    return () => window.removeEventListener('solomon:reload-lead', handler);
  }, [leadId, fetchLead]);

  // ── Enrich now (Token 2 lookup; ~$0.21/call) ────────────────
  const handleEnrich = useCallback(async (forceRecompute = false) => {
    setEnriching(true);
    setEnrichError(null);
    setEnrichStatus(null);
    setEnrichSuppressed(null);
    try {
      const res = await authFetch(`${API_BASE}/api/leads/${leadId}/enrich`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ forceRecompute }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || `HTTP ${res.status}`);
      const d = data.data || {};

      // Auto-archived by the post-enrichment contactability gate. The lead is
      // gone from the active list, so re-fetching it would 404 into a
      // misleading "lead not found". Explain WHY instead of refetching.
      if (d.suppressed) {
        const LABELS = {
          tcpa_litigator:  'a known TCPA litigator',
          tcpa_dnc:        'on the Do-Not-Call (DNC) registry',
          all_phones_dnc:  'reachable only at numbers on the Do-Not-Call list',
          no_phone:        'has no contactable phone number',
        };
        const reasons = (d.suppressReasons || []).map((r) => LABELS[r] || r);
        const why = reasons.length ? reasons.join('; ') : 'not legally contactable';
        setEnrichSuppressed(
          `This lead is ${why}. It cannot legally be contacted, so it has been archived ` +
          `(the enrichment data was still saved). It will no longer appear in your active list.`
        );
        return;
      }

      // Money guard hit: recently enriched, no new charge.
      if (d.cached) {
        setEnrichStatus('Already enriched recently — used cached data, no charge. Use "Force fresh re-pull" for new data.');
        await fetchLead();
        return;
      }

      const tcpa = d.tcpa || {};
      const flagged = tcpa.is_tcpa_litigator || tcpa.tcpa_dnc;
      setEnrichStatus(flagged
        ? '⚠ Enriched — TCPA suppression flagged. DO NOT contact.'
        : `✓ Enriched (${d.updatedColumns?.length || 0} fields updated)`);
      // Refresh the lead row so the new fields render
      await fetchLead();
    } catch (err) {
      // The lead was archived out from under this view (e.g. suppressed in
      // another tab, or this detail is stale). Don't show the raw
      // "lead_not_found" — explain it and return to the refreshed list.
      if (err.message === 'lead_not_found' || /lead not found/i.test(err.message || '')) {
        setEnrichError('This lead is no longer in your active list — it was archived (already enriched and flagged un-contactable). Returning to your leads…');
        setTimeout(() => onBack?.(), 1800);
      } else {
        setEnrichError(err.message || 'Enrichment failed');
      }
    } finally {
      setEnriching(false);
    }
  }, [authFetch, leadId, fetchLead, onBack]);

  // ── Notes: debounced save (500ms) + save on blur ────────────
  const persistNotes = useCallback(async (value) => {
    if (value === notesInitialRef.current) {
      setNotesStatus(null);
      return;
    }
    setNotesStatus('saving');
    try {
      const res = await authFetch(`${API_BASE}/api/leads/${leadId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes: value }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || `HTTP ${res.status}`);
      notesInitialRef.current = value;
      setNotesStatus('saved');
      setTimeout(() => setNotesStatus((s) => (s === 'saved' ? null : s)), 1500);
    } catch (err) {
      setNotesStatus('error');
      console.warn('[LeadDetail/notes] save failed:', err.message);
    }
  }, [authFetch, leadId]);

  const onNotesChange = useCallback((e) => {
    const v = e.target.value;
    setNotesDraft(v);
    if (notesSaveTimer.current) clearTimeout(notesSaveTimer.current);
    notesSaveTimer.current = setTimeout(() => persistNotes(v), 500);
  }, [persistNotes]);

  const onNotesBlur = useCallback(() => {
    if (notesSaveTimer.current) {
      clearTimeout(notesSaveTimer.current);
      notesSaveTimer.current = null;
    }
    persistNotes(notesDraft);
  }, [notesDraft, persistNotes]);

  // ── Quiet refresh ───────────────────────────────────────────
  // Re-read lead + history WITHOUT flipping the full-screen loading state, so
  // an in-place state change (disposition log or deal-stage move) updates the
  // pill and the history timeline without unmounting the child that triggered
  // it (which would discard its success toast). Deliberately never writes the
  // shared `error` state — a refresh hiccup must not trip the fatal-load guard
  // into the misleading "archived" view.
  const refreshQuiet = useCallback(async () => {
    try {
      const [leadRes, histRes] = await Promise.all([
        authFetch(`${API_BASE}/api/leads/${leadId}`),
        authFetch(`${API_BASE}/api/leads/${leadId}/history`),
      ]);
      const leadData = await leadRes.json();
      const histData = await histRes.json();
      if (leadRes.ok && leadData.success) setLead(leadData.data);
      if (histRes.ok && histData.success) setHistory(histData.data || []);
    } catch (err) {
      console.warn('[LeadDetail/refreshQuiet]', err.message);
    }
  }, [authFetch, leadId]);

  // ── Memoised derived data ──────────────────────────────────
  const altPhones = useMemo(() => {
    if (!lead?.phone_alternates) return [];
    if (Array.isArray(lead.phone_alternates)) return lead.phone_alternates;
    try { return JSON.parse(lead.phone_alternates); } catch { return []; }
  }, [lead?.phone_alternates]);

  const sourceMetaIsPopulated = lead?.source_metadata
    && typeof lead.source_metadata === 'object'
    && Object.keys(lead.source_metadata).length > 0;

  // ── Render ─────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="leads-list-loading">
        <div className="leads-import-pulse" />
        <div>Loading lead…</div>
      </div>
    );
  }
  if (error || !lead) {
    return (
      <div>
        <button className="lead-detail-back" onClick={onBack}>
          <ChevronLeft /> Back to leads
        </button>
        <div className="leads-import-error">
          {!error || /lead not found/i.test(error)
            ? 'This lead has been archived and is no longer in your active list (likely enriched and flagged un-contactable).'
            : error}
        </div>
      </div>
    );
  }

  const handleDelete = async () => {
    const ownerLabel = lead.owner_full_name || lead.property_address || 'this lead';
    if (!window.confirm(`Delete ${ownerLabel}?\n\nThis archives the lead (soft delete). The row stays in the database for audit but won't appear in queues, lists, or briefings.`)) {
      return;
    }
    try {
      const r = await authFetch(`${API_BASE}/api/leads/${leadId}`, { method: 'DELETE' });
      const json = await r.json();
      if (!r.ok || !json.success) throw new Error(json.error || 'Failed to delete lead');
      onBack();
    } catch (err) {
      window.alert(`Delete failed: ${err.message}`);
    }
  };

  return (
    <div className="lead-detail">
      <div className="lead-detail-topbar">
        <button className="lead-detail-back" onClick={onBack}>
          <ChevronLeft /> Back to leads
        </button>
        <span className={`leads-state-pill ${STATE_CLASS[lead.state] || ''}`}>
          {STATE_LABELS[lead.state] || lead.state}
        </span>
        {lead.lead_origin === 'public_records' && (
          <span
            className="leads-origin-badge"
            title={lead.signal_provenance === 'verified'
              ? 'Ground-truth: verified public-records filing (foreclosure / tax-sale / code case)'
              : 'Sourced from public records'}
          >
            Public records{lead.signal_provenance === 'verified' ? ' · verified' : ''}
          </span>
        )}
        <button
          className="lead-detail-delete-btn"
          onClick={handleDelete}
          title="Archive this lead (soft delete — row stays for audit, hidden from queues/lists)"
        >
          Delete
        </button>
      </div>

      {/* TCPA suppression warning — renders only when flagged. Critical
          legal-exposure surface; intentionally above the fold. */}
      {(lead.is_tcpa_litigator || lead.tcpa_dnc) && (
        <div className="lead-detail-tcpa-warning" role="alert">
          <div className="lead-detail-tcpa-icon">⚠</div>
          <div className="lead-detail-tcpa-body">
            <div className="lead-detail-tcpa-title">DO NOT CONTACT — TCPA suppression</div>
            <div className="lead-detail-tcpa-reasons">
              {lead.is_tcpa_litigator && (
                <div>• Owner is a known TCPA litigator. Contact triggers $500–$1,500 per message in damages.</div>
              )}
              {lead.tcpa_dnc && (
                <div>• Owner is on the TCPA Do-Not-Contact registry.</div>
              )}
              <div className="lead-detail-tcpa-note">
                All outreach (SMS, RVM, voice) is hard-blocked at the executor level.
                isContactable() rejects, action_log records suppression event.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Self-booked / scheduled callback — surfaced above the fold so the
          operator sees WHEN at a glance. Shown in Eastern (the funnel's tz). */}
      {lead.next_appointment_at && (
        <div className="lead-detail-appt" role="status">
          📅 Callback booked — {new Intl.DateTimeFormat('en-US', {
            timeZone: 'America/New_York', weekday: 'long', month: 'long',
            day: 'numeric', hour: 'numeric', minute: '2-digit',
          }).format(new Date(lead.next_appointment_at))} ET
        </div>
      )}

      {/* Verified distress factor — the ground-truth reason from the public
          record (foreclosure / tax-sale / code case). Above the fold: it's the
          reason to call, and it's why these leads out-convert modeled ones. */}
      {lead.lead_origin === 'public_records' && lead.source_metadata?.distress_factor && (
        <div className="lead-detail-distress" role="status">
          <div className="lead-detail-distress-top">
            <span className="lead-detail-distress-badge">Verified distress</span>
            <span className="lead-detail-distress-factor">{lead.source_metadata.distress_factor}</span>
          </div>
          <div className="lead-detail-distress-meta">
            {lead.source_metadata.judgment_amount != null && (
              <span><span className="lead-detail-k">Judgment</span> {formatCurrency(lead.source_metadata.judgment_amount)}</span>
            )}
            {lead.source_metadata.plaintiff && (
              <span><span className="lead-detail-k">Filed by</span> {lead.source_metadata.plaintiff}</span>
            )}
            {lead.source_metadata.cause_number && (
              <span><span className="lead-detail-k">Cause</span> {lead.source_metadata.cause_number}</span>
            )}
            {lead.source_metadata.source_url && (
              <a className="lead-detail-distress-src" href={lead.source_metadata.source_url}
                 target="_blank" rel="noreferrer">public record ↗</a>
            )}
          </div>
        </div>
      )}

      {/* Enrichment controls */}
      <section className="lead-detail-section lead-detail-enrich-section sol-card">
        <h3 className="lead-detail-h">
          Enrichment
          {lead.comps_pulled_at && (
            <span className="lead-detail-enrich-stamp">
              last pulled {formatRelativeTime(lead.comps_pulled_at)}
            </span>
          )}
        </h3>
        <div className="lead-detail-enrich-row">
          <div className="button-with-cost">
            <button
              className="btn-primary lead-detail-enrich-btn"
              disabled={enriching}
              title={lead.comps_pulled_at
                ? 'Reuse cached enrichment if pulled within 30 days — no charge. Use "Force fresh re-pull" to pay for new data.'
                : 'Run BatchData Token 2 lookup (~$0.51) — populates valuation, phones, emails, mailing address, and TCPA compliance flags.'}
              onClick={() => handleEnrich(false)}
            >
              {enriching ? 'Enriching…' : (lead.comps_pulled_at ? 'Refresh enrichment' : 'Enrich now')}
            </button>
            <CostSubtitle buttonId="enrich_now" />
            {lead.comps_pulled_at && (
              <button
                className="btn-secondary lead-detail-enrich-force"
                disabled={enriching}
                title="Pay for a fresh BatchData skip-trace even though this lead was enriched recently."
                onClick={() => setEnrichDialogOpen(true)}
              >
                Force fresh re-pull ($0.51)
              </button>
            )}
            <ConfirmCostDialog
              isOpen={enrichDialogOpen}
              buttonId="enrich_now"
              leadId={lead.id}
              onConfirm={() => { setEnrichDialogOpen(false); handleEnrich(true); }}
              onCancel={() => setEnrichDialogOpen(false)}
            />
          </div>
        </div>
        {enrichSuppressed && (
          <div className="lead-detail-error" role="alert">🚫 {enrichSuppressed}</div>
        )}
        {enrichError  && <div className="lead-detail-error">{enrichError}</div>}
        {enrichStatus && <div className="lead-detail-status">{enrichStatus}</div>}
        {!lead.comps_pulled_at && (
          <div className="lead-detail-note">
            Lead is not yet enriched — phones, emails, and TCPA flags are unverified.
            Outreach is blocked until enrichment runs (no phone in DB, fail-closed).
          </div>
        )}
      </section>

      {/* Property */}
      <section className="lead-detail-section sol-card">
        <h3 className="lead-detail-h">Property</h3>
        <div className="lead-detail-address">
          {lead.property_address}
        </div>
        <div className="lead-detail-locality">
          {[lead.property_city, lead.property_state, lead.property_zip].filter(Boolean).join(' · ')}
          {lead.property_county && <span className="lead-detail-county"> · {lead.property_county} County</span>}
        </div>
        <div className="lead-detail-grid">
          <div>
            <div className="lead-detail-k">Estimated value</div>
            <div className="lead-detail-v">{formatCurrency(lead.property_value_estimate)}</div>
          </div>
          <div>
            <div className="lead-detail-k">Equity</div>
            <div className="lead-detail-v">{formatCurrency(lead.equity_estimate)}</div>
          </div>
          <div>
            <div className="lead-detail-k">ARV</div>
            <div className="lead-detail-v">{formatCurrency(lead.estimated_arv)}</div>
          </div>
          <div>
            <div className="lead-detail-k">MAO</div>
            <div className="lead-detail-v">
              {(() => {
                // Prefer the live calculator_state.outputs.target.mao — that's
                // what the operator sees in the calculator. estimated_max_offer
                // is a legacy column maintained for backwards-compat with
                // imports; the post-Phase-1.5 calculator now persists outputs
                // to calculator_state JSONB (including mao), so this read is live.
                const calcTarget = lead?.calculator_state?.outputs?.target?.mao;
                const mao = calcTarget != null ? calcTarget : lead?.estimated_max_offer;
                if (mao != null) return formatCurrency(mao);
                return <span className="lead-detail-note">set ARV + condition below</span>;
              })()}
            </div>
          </div>
        </div>
      </section>

      {/* Owner */}
      <section className="lead-detail-section sol-card">
        <h3 className="lead-detail-h">Owner</h3>
        <div className="lead-detail-owner">{lead.owner_full_name || '—'}</div>
        <div className="lead-detail-phone-primary">{lead.phone_primary || '—'}</div>
        {altPhones.length > 0 && (
          <div className="lead-detail-alt-phones">
            {altPhones.map((p, i) => (
              <span key={i} className="lead-detail-alt-phone">{p.phone || p}</span>
            ))}
          </div>
        )}
        {lead.email && <div className="lead-detail-email">{lead.email}</div>}
        {lead.co_owner_name && (
          <div className="lead-detail-co">
            <span className="lead-detail-k">Co-owner</span>{' '}
            {lead.co_lead_id ? (
              <button className="lead-detail-link" onClick={() => onNavigate(lead.co_lead_id)}>
                {lead.co_owner_name} →
              </button>
            ) : (
              <span>{lead.co_owner_name}</span>
            )}
          </div>
        )}
      </section>

      {/* Deal stage — single pipeline state control, sits under the contact
          identity so the operator can advance the deal in one click. Rendered
          even for TCPA-suppressed leads so they can still be marked dead / DNC
          (the logger + calculator below hide under suppression). */}
      <DealStageStepper leadId={leadId} lead={lead} onChanged={refreshQuiet} />

      {/* Log Call — Tier 1 cold-call workflow. Hidden if TCPA-suppressed.
          onLogged uses the quiet refresh so the success toast survives. */}
      {!(lead.is_tcpa_litigator || lead.tcpa_dnc) && (
        <DispositionLogger leadId={leadId} lead={lead} onLogged={refreshQuiet} />
      )}

      {/* Offer calculator — Phase 1.5 §4A. Hidden if the lead is
          TCPA-suppressed (no point computing offers we can never make). */}
      {!(lead.is_tcpa_litigator || lead.tcpa_dnc) && (
        <LeadCalculator leadId={leadId} lead={lead} />
      )}

      {/* Notes */}
      <section className="lead-detail-section sol-card">
        <h3 className="lead-detail-h">
          Notes
          {notesStatus && (
            <span className={`lead-detail-notes-status status-${notesStatus}`}>
              {notesStatus === 'saving' && 'saving…'}
              {notesStatus === 'saved'  && 'saved'}
              {notesStatus === 'error'  && 'save failed'}
            </span>
          )}
        </h3>
        <textarea
          className="lead-detail-notes"
          value={notesDraft}
          onChange={onNotesChange}
          onBlur={onNotesBlur}
          placeholder="Operator notes — saved automatically."
          rows={5}
        />
      </section>

      {/* History */}
      <section className="lead-detail-section sol-card">
        <button className="lead-detail-collapse" onClick={() => setShowHistory((v) => !v)}>
          <CaretDown open={showHistory} />
          <span>State history ({history.length})</span>
        </button>
        {showHistory && (
          history.length === 0 ? (
            <div className="lead-detail-note">No transitions recorded yet.</div>
          ) : (
            <ul className="lead-detail-timeline">
              {history.map((h) => (
                <li key={h.id} className="lead-detail-timeline-row">
                  <div className="lead-detail-timeline-arrow">
                    <span className={`leads-state-pill small ${STATE_CLASS[h.from_state] || ''}`}>
                      {STATE_LABELS[h.from_state] || h.from_state || '—'}
                    </span>
                    <span className="lead-detail-arrow">→</span>
                    <span className={`leads-state-pill small ${STATE_CLASS[h.to_state] || ''}`}>
                      {STATE_LABELS[h.to_state] || h.to_state}
                    </span>
                  </div>
                  <div className="lead-detail-timeline-meta">
                    <span className="lead-detail-trigger">{h.triggered_by}</span>
                    <span className="lead-detail-time">{formatRelativeTime(h.created_at)}</span>
                  </div>
                  {h.reason && <div className="lead-detail-timeline-reason">{h.reason}</div>}
                </li>
              ))}
            </ul>
          )
        )}
      </section>

      {/* Source metadata */}
      <section className="lead-detail-section sol-card">
        <button className="lead-detail-collapse" onClick={() => setShowMeta((v) => !v)}>
          <CaretDown open={showMeta} />
          <span>Source</span>
        </button>
        {showMeta && (
          <div className="lead-detail-source">
            <div><span className="lead-detail-k">Source:</span> {lead.source}</div>
            {lead.source_list_name && <div><span className="lead-detail-k">List:</span> {lead.source_list_name}</div>}
            <div><span className="lead-detail-k">Imported:</span> {formatRelativeTime(lead.imported_at)}</div>
            {sourceMetaIsPopulated && (
              <details className="lead-detail-source-raw">
                <summary>Raw CSV row</summary>
                <pre>{JSON.stringify(lead.source_metadata, null, 2)}</pre>
              </details>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
